import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FiUpload, FiCheckCircle, FiAlertCircle, FiX, FiImage } from 'react-icons/fi';
import './UploadModal.css';

const AUTH_ENDPOINT =
  process.env.REACT_APP_IMAGEKIT_AUTH_ENDPOINT ||
  'https://imagekit-auth-server-ib8y.onrender.com/auth';

const PUBLIC_KEY =
  process.env.REACT_APP_IMAGEKIT_PUBLIC_KEY ||
  'public_3Pd8+8b9G0fq9ZWATOe4pmquWyI=';

const IMAGEKIT_UPLOAD_URL = 'https://upload.imagekit.io/api/v1/files/upload';

/* ─── Shared upload logic hook ─── */
function useImageKitUpload({ userId, type, onUploadComplete, onUploadStart, onClose }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [status, setStatus] = useState('idle'); // idle | ready | uploading | success | error
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // Cleanup object URL on unmount / change
  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  const validateAndSetFile = useCallback((selected) => {
    if (!selected) return;
    if (!selected.type.match('image.*')) {
      alert('Please select an image file (JPEG, PNG, etc.)');
      return;
    }
    if (selected.size > 5 * 1024 * 1024) {
      alert('Image must be smaller than 5 MB');
      return;
    }
    if (preview) URL.revokeObjectURL(preview);
    setFile(selected);
    setPreview(URL.createObjectURL(selected));
    setStatus('ready');
  }, [preview]);

  const handleFileChange = (e) => validateAndSetFile(e.target.files[0]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    validateAndSetFile(e.dataTransfer.files[0]);
  };

  const handleUpload = async () => {
    if (!file) return;
    try {
      setStatus('uploading');
      setProgress(0);
      onUploadStart?.();

      // Step 1: Get auth params from your server
      const res = await fetch(AUTH_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error(`Auth request failed: ${res.statusText}`);

      const authData = await res.json();
      if (!authData.token || !authData.signature || !authData.expire) {
        throw new Error(authData.error || 'Invalid auth parameters from server');
      }

      // Step 2: Build the fileName
      const ext = file.name.split('.').pop();
      const baseName = `${type || 'upload'}_${userId || 'user'}_${Date.now()}`;
      const fileName = `${baseName}.${ext}`;

      // Step 3: Upload directly to ImageKit API
      const formData = new FormData();
      formData.append('file', file);                     // the actual image
      formData.append('fileName', fileName);             // required by ImageKit
      formData.append('publicKey', PUBLIC_KEY);          // your public key
      formData.append('signature', authData.signature);  // from auth server
      formData.append('expire', authData.expire);        // from auth server
      formData.append('token', authData.token);          // from auth server

      // Optional: organise uploads into a folder
      if (userId) formData.append('folder', `/${type || 'uploads'}/${userId}`);

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percent = Math.round((e.loaded / e.total) * 100);
          setProgress(percent);
        }
      });

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const result = JSON.parse(xhr.responseText);
            setProgress(100);
            setStatus('success');
            // ImageKit returns the CDN URL in result.url
            onUploadComplete?.(result.url);
            setTimeout(() => onClose?.(), 900);
          } catch (err) {
            console.error('Parse error:', err);
            setStatus('error');
            setProgress(0);
          }
        } else {
          console.error('Upload error response:', xhr.responseText);
          setStatus('error');
          setProgress(0);
        }
      };

      xhr.onerror = () => {
        setStatus('error');
        setProgress(0);
      };

      xhr.open('POST', IMAGEKIT_UPLOAD_URL);
      // Do NOT set Content-Type header — let the browser set it with the correct boundary for FormData
      xhr.send(formData);

    } catch (err) {
      console.error('Upload failed:', err);
      setStatus('error');
      setProgress(0);
    }
  };

  const reset = useCallback(() => {
    if (preview) URL.revokeObjectURL(preview);
    setFile(null);
    setPreview('');
    setStatus('idle');
    setProgress(0);
    setDragOver(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [preview]);

  return {
    file, preview, status, progress, dragOver, setDragOver,
    fileInputRef, handleFileChange, handleDrop, handleUpload, reset,
  };
}

/* ─── The Modal (rendered via portal) ─── */
function UploadModalContent({ title, onClose, userId, type, onUploadComplete, onUploadStart }) {
  const {
    file, preview, status, progress, dragOver, setDragOver,
    fileInputRef, handleFileChange, handleDrop, handleUpload, reset,
  } = useImageKitUpload({ userId, type, onUploadComplete, onUploadStart, onClose });

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape' && status !== 'uploading') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose, status]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && status !== 'uploading') onClose();
  };

  return (
    <div className="ik-overlay" onClick={handleOverlayClick}>
      <div className="ik-modal" role="dialog" aria-modal="true" aria-labelledby="ik-modal-title">
        {/* Header */}
        <div className="ik-modal-header">
          <p className="ik-modal-title" id="ik-modal-title">{title || 'Upload Image'}</p>
          <button
            className="ik-close-btn"
            onClick={onClose}
            disabled={status === 'uploading'}
            aria-label="Close"
          >
            <FiX />
          </button>
        </div>

        {/* Body */}
        <div className="ik-modal-body">
          {/* Drop zone */}
          {status !== 'success' && (
            <div
              className={`ik-dropzone${dragOver ? ' drag-over' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="ik-dropzone-input"
                onChange={handleFileChange}
                disabled={status === 'uploading'}
              />
              <FiImage className="ik-dropzone-icon" />
              <p className="ik-dropzone-text">
                {file ? file.name : 'Click or drag & drop an image here'}
              </p>
              <p className="ik-dropzone-hint">JPEG, PNG, WebP • Max 5 MB</p>
            </div>
          )}

          {/* Preview */}
          {preview && status !== 'success' && (
            <div className="ik-preview-wrap">
              <img src={preview} alt="Preview" className="ik-preview-img" />
              {status !== 'uploading' && (
                <button className="ik-preview-change" onClick={reset}>Change</button>
              )}
            </div>
          )}

          {/* Progress */}
          {status === 'uploading' && (
            <div className="ik-progress-wrap">
              <div className="ik-progress-bar-track">
                <div className="ik-progress-bar-fill" style={{ width: `${progress}%` }} />
              </div>
              <p className="ik-progress-label">Uploading… {progress}%</p>
            </div>
          )}

          {/* Status messages */}
          {status === 'success' && (
            <div className="ik-status success">
              <FiCheckCircle size={16} />
              Image uploaded successfully!
            </div>
          )}
          {status === 'error' && (
            <div className="ik-status error">
              <FiAlertCircle size={16} />
              Upload failed. Please try again.
            </div>
          )}
        </div>

        {/* Footer */}
        {status !== 'success' && (
          <div className="ik-modal-footer">
            <button
              className="ik-btn ik-btn-cancel"
              onClick={onClose}
              disabled={status === 'uploading'}
            >
              Cancel
            </button>
            <button
              className="ik-btn ik-btn-upload"
              onClick={handleUpload}
              disabled={!file || status === 'uploading'}
            >
              <FiUpload style={{ marginRight: 6, verticalAlign: 'middle' }} />
              {status === 'uploading' ? 'Uploading…' : status === 'error' ? 'Retry' : 'Upload'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Public component ─── */
const ImageUploader = ({
  userId,
  type,
  currentImageUrl,
  onUploadComplete,
  onUploadStart,
  label = 'Select Image',
  required = false,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [uploadedUrl, setUploadedUrl] = useState(currentImageUrl || '');

  useEffect(() => {
    setUploadedUrl(currentImageUrl || '');
  }, [currentImageUrl]);

  const handleDone = (url) => {
    setUploadedUrl(url);
    onUploadComplete?.(url);
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    setUploadedUrl('');
    onUploadComplete?.('');
  };

  return (
    <>
      {/* Compact trigger button */}
      <button
        type="button"
        className={`ik-trigger${uploadedUrl ? ' has-image' : ''}`}
        onClick={() => setModalOpen(true)}
      >
        {uploadedUrl ? (
          <>
            <FiCheckCircle size={15} />
            <span>{label === 'Select Image' ? 'Image uploaded' : `${label} (uploaded)`}</span>
            <span
              role="button"
              aria-label="Remove image"
              style={{ marginLeft: 4, opacity: 0.7, cursor: 'pointer' }}
              onClick={handleRemove}
            >
              <FiX size={13} />
            </span>
          </>
        ) : (
          <>
            <FiUpload size={14} />
            <span>{label}</span>
            {required && <span style={{ color: '#ef4444', marginLeft: 2 }}>*</span>}
          </>
        )}
      </button>

      {/* Portal modal */}
      {modalOpen &&
        createPortal(
          <UploadModalContent
            title={uploadedUrl ? 'Replace Image' : 'Upload Image'}
            onClose={() => setModalOpen(false)}
            userId={userId}
            type={type}
            onUploadComplete={(url) => {
              handleDone(url);
              setModalOpen(false);
            }}
            onUploadStart={onUploadStart}
          />,
          document.body
        )}
    </>
  );
};

export default ImageUploader;
